/* eslint react-hooks/exhaustive-deps: "warn" */
import { useEffect, useRef, useState } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';
import { useIntersection } from 'hooks/useIntersection';

import { Button } from 'ui/atoms/Button/Button';
import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './ManufacturingHero.module.scss';

type Stat = {
  value: number;
  suffix: string;
  label: string;
};

type Field = {
  key: string;
  value: string;
  mono?: boolean;
  status?: boolean;
  source: string;
  date: string;
};

type Translate = (key: string) => string;

const getStats = (t: Translate): Stat[] => [
  { value: 400, suffix: '+', label: t('government registries') },
  { value: 200, suffix: '+', label: t('countries and territories') },
  { value: 600, suffix: 'M+', label: t('companies on file') },
];

// Sample supplier record shown in the hero widget: field labels are translated,
// specimen values / registry sources / dates are left as-is.
const getFields = (t: Translate): Field[] => [
  {
    key: t('Legal name'),
    value: 'Meridian Components Vietnam Co., Ltd',
    source: 'National Business Registration Portal',
    date: '11 Mar 2026',
  },
  {
    key: t('Enterprise code'),
    value: 'SAMPLE-0106482391',
    mono: true,
    source: 'National Business Registration Portal',
    date: '06 Jun 2016',
  },
  {
    key: t('Status'),
    value: 'Active',
    status: true,
    source: 'National Business Registration Portal',
    date: 'checked 03 Aug 2026',
  },
  {
    key: t('Shareholder'),
    value: 'Meridian Industrial (HK) Limited · 100%',
    source: 'Companies Registry, Hong Kong',
    date: '19 Jan 2026',
  },
  {
    key: t('Ultimate parent'),
    value: 'Meridian Group AB',
    source: 'Bolagsverket, Sweden',
    date: '22 Apr 2026',
  },
];

const StatCounter = ({ value, suffix, label }: Stat) => {
  const ref = useRef<HTMLDivElement>(null);
  const revealed = useIntersection(ref, { threshold: 0.4 });
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!revealed) {
      return;
    }

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setCount(value);
      return;
    }

    const duration = 1100;
    const start = performance.now();

    let frame: number;

    const tick = (now: number) => {
      const progress = Math.min(1, (now - start) / duration);
      const eased = 1 - (1 - progress) ** 3;
      setCount(Math.round(eased * value));

      if (progress < 1) {
        frame = requestAnimationFrame(tick);
      }
    };

    frame = requestAnimationFrame(tick);

    return () => cancelAnimationFrame(frame);
  }, [revealed, value]);

  return (
    <div className={styles.stat} ref={ref}>
      <b>
        {count}
        {suffix}
      </b>
      <span>{label}</span>
    </div>
  );
};

export const ManufacturingHero = () => {
  const { t } = useInt();
  const recordRef = useRef<HTMLDivElement>(null);
  const revealed = useIntersection(recordRef, { threshold: 0.15 });
  const [hovered, setHovered] = useState<number | null>(null);

  return (
    <Section className={styles.root}>
      <div className={styles.grid}>
        <div className={styles.copy}>
          <TitleBlock
            size="page"
            align="left"
            title={t('Every supplier resolved to a government registry.')}
            description={t(
              'Send us your vendor master. We match each record to the registered legal entity behind it, return its ownership up to the ultimate parent, and tell you the day any of it changes.',
            )}
          />
          <div className={styles.actions}>
            <Button type="blue" size="xxl" href="/book-a-demo">
              {t('Match a sample of your vendor file')}
            </Button>
            <Button type="dark-outline" size="xxl" href="#fields">
              {t('View our data')}
            </Button>
          </div>

          <div className={styles.stats}>
            {getStats(t).map((stat) => (
              <StatCounter key={stat.label} {...stat} />
            ))}
            <div className={styles.stat}>
              <b>{t('Daily')}</b>
              <span>{t('registry sync')}</span>
            </div>
          </div>
        </div>

        <div className={cn(styles.record, revealed && styles.recordOn)} ref={recordRef}>
          <div className={styles.recordCap}>
            <span className={styles.recordCapT}>{t('Supplier record, resolved')}</span>
            <span className={styles.recordCapS}>{t('Sample data')}</span>
          </div>

          <div className={styles.recordSub}>
            <span className={styles.recordSubK}>{t('Submitted')}</span>
            <span className={styles.recordSubV}>MERIDIAN COMPONENTS VIETNAM CO LTD · BINH DUONG · VENDOR 4471-B</span>
          </div>

          <div className={styles.recordRows}>
            {getFields(t).map((field, i) => (
              <div
                key={field.key}
                className={cn(styles.recordRow, hovered === i && styles.recordRowOn)}
                style={{ transitionDelay: revealed ? `${i * 70}ms` : '0ms' }}
                onMouseEnter={() => setHovered(i)}
                onMouseLeave={() => setHovered(null)}
              >
                <span className={styles.recordK}>{field.key}</span>
                <span className={cn(styles.recordV, field.mono && styles.mono)}>
                  {field.status && <span className={styles.recordDot} />}
                  {field.value}
                </span>
                <span className={styles.recordS}>
                  {field.source}
                  <i>{field.date}</i>
                </span>
              </div>
            ))}
          </div>

          <p className={styles.recordFoot}>
            {t('Every value carries the register it was read from and the date it was filed.')}
          </p>
        </div>
      </div>
    </Section>
  );
};
